import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faSort,
  faSortDown,
  faSortUp,
  IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { ReactElement, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Observable } from 'rxjs';
import FieldTypes from '../../../enums/field-types';
import { useQuery } from '../../../hooks/use-query';
import Paginate from '../../../interfaces/paginate';
import { PartialAbility } from '../../../router/abilityContext';
import { ListParams } from '../../../services/http-client';
import { accessData, can, route } from '../../../services/utils';
import { RootState } from '../../../store/store';
import Paging from '../paging';
import Spinner from '../spinner';
import css from './table.module.css';

export interface TableColumnGroup<T> {
  header: string;
  startsAtAccessor: keyof T | (keyof T)[]; // since we don't specify keys manually (headers can be the same)
  colspan: number;
  className?: string;
}

export interface TableColumn<T> {
  editable?: (row: T) => ReactNode;
  accessor: keyof T | (keyof T)[] | string;
  aggregateAccessor?: string;
  header: string;
  sortable?: boolean;
  filter?: object;
  translatable?: any;
  translationPrefix?: string;
  dataType?: FieldTypes;
  className?: string;
  headerClassName?: string;
  valuable?: (item: any, v: T) => any;
  onRowClick?: any;
  cell?: (row: T) => JSX.Element;
}

interface FilterTotalsParams<T> {
  recordName: string;
  fullText?: {
    tp: (key: string, params: { [key: string]: string | number }) => string;
    key?: string;
    keys?: {
      key: string;
      value: (data: T[]) => string | number;
    }[];
  };
}

export default function Table<T>({
  data,
  request,
  keyAccessor,
  noDataMessage = 'No entries found',
  columnGroups = null,
  columns,
  showAggregates = true,
  aggregateLabels,
  viewRoute,
  paginate = true,
  selectedRowsState = null,
  rowExpander = null,
  rowClassSetter = null,
  toolbar,
  viewRecordAbility = null,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  tooltip,
  tooltipContainerProps,
  inTotal = false,
  totalValueCols,
  filteredTotalsParams = null,
  onRemove,
  showRemove = false,
  notShowSelectedChecked = '',
  responseData = null,
  tableAction = null,
  disableAllPagination = false,
  rowDisabled = () => false,
  rowCheckable = () => true,
}: {
  onMouseEnter?: (row: T) => void;
  onMouseLeave?: (row: T) => void;
  data?: T[];
  request?: (
    page: number,
    sort: string,
    desc: boolean,
    filter?: { [key: string]: any }
  ) => Observable<Paginate<T>>;
  keyAccessor?: keyof T | ((t: T) => any);
  noDataMessage?: string;
  columnGroups?: TableColumnGroup<T>[];
  columns: TableColumn<T>[];
  showAggregates?: boolean;
  aggregateLabels?: { [key: string]: string };
  viewRoute?: string | ((row: T) => string);
  paginate?: boolean;
  selectedRowsState?: [T[], React.Dispatch<React.SetStateAction<T[]>>];
  rowExpander?: (row: T) => JSX.Element;
  rowClassSetter?: (row: T) => string;
  toolbar?: (params: ListParams) => JSX.Element;
  viewRecordAbility?: PartialAbility;
  tooltip?: ReactElement;
  tooltipContainerProps?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >;
  inTotal?: boolean;
  totalValueCols?: string[];
  filteredTotalsParams?: FilterTotalsParams<T>;
  onRemove?: (index: number, data: T[]) => void;
  showRemove?: boolean;
  notShowSelectedChecked?: string;
  responseData?: (response) => void;
  tableAction?: JSX.Element;
  disableAllPagination?: boolean;
  rowDisabled?: (row: T) => boolean;
  rowCheckable?: (row: T) => boolean;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const [response, setResponse] = useState<Paginate<T>>(null);
  const [params, page, sort, desc] = useQuery();
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [authorizedViewRoute, setAuthorizedViewRoute] = useState<
    string | ((row: T) => string) | null
  >(null);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  useEffect(() => {
    if (data) {
      setResponse({ data: data } as Paginate<T>);
      return;
    }
  }, [data]);

  useEffect(
    () => {
      if (!request && !data) {
        return;
      }
      if (!request && data) {
        setResponse(null);
        setResponse({
          data,
        } as Paginate<T>);
      } else {
        setResponse(null);
        // unselect all rows on data reload
        selectedRowsState && selectedRowsState[1]([]);
        const subscription = request(page, sort, desc, params).subscribe(
          (response) => {
            // on 2nd and further pages, Illuminate\Pagination\LengthAwarePaginator
            // returns associative array with continuing keys from the previous page,
            // to not have to deal with two data types, convert object to array here
            if (response.data && !Array.isArray(response.data)) {
              response.data = Object.values(response.data);
            }
            setResponse(response);
          }
        );

        if (viewRecordAbility && can(farmUser, viewRecordAbility)) {
          setAuthorizedViewRoute(() => viewRoute);
        }

        if (
          viewRecordAbility &&
          Object.values(viewRecordAbility)[0] === 'public'
        ) {
          setAuthorizedViewRoute(() => viewRoute);
        }

        return () => subscription.unsubscribe();
      }
    },
    // TODO: find a better solution
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request, desc, page, sort, params, data]
  );

  function key(value: T) {
    if (
      typeof keyAccessor === 'string' ||
      typeof keyAccessor === 'number' ||
      typeof keyAccessor === 'symbol'
    ) {
      return value[keyAccessor as keyof T];
    } else {
      return keyAccessor(value);
    }
  }

  function thead() {
    function icon(accessor: string): IconDefinition {
      const params = new URLSearchParams(location.search);
      if (params.get('sort') !== accessor) {
        return faSort;
      } else {
        if (params.get('desc') === 'true') {
          return faSortDown;
        } else {
          return faSortUp;
        }
      }
    }

    function search(search: string, accessor: string): string {
      const params = new URLSearchParams(search);

      if (params.get('sort') !== accessor) {
        params.set('sort', accessor);
        params.delete('desc');
      } else {
        if (params.get('desc') === 'true') {
          params.delete('desc');
        } else {
          params.set('desc', 'true');
        }
      }
      params.delete('page');

      return '?'.concat(params.toString());
    }

    function selectAllRows(event: React.ChangeEvent<HTMLInputElement>) {
      const setSelectedRows = selectedRowsState[1];
      if (event.target.checked) {
        setSelectedRows(response.data);
      } else {
        setSelectedRows([]);
      }
    }

    function hasAllPageRowSelector() {
      return selectedRowsState !== null && response?.data;
    }

    function allPageRowSelector() {
      if (hasAllPageRowSelector()) {
        const selectedRows = selectedRowsState[0];
        return (
          <th scope="col" key={'id'}>
            {!disableAllPagination && (
              <input
                type="checkbox"
                id={'selectAllRows'}
                name={'selectAllRows'}
                checked={selectedRows.length === response.data.length}
                className={css['checkbox']}
                onChange={(event) => selectAllRows(event)}
              />
            )}
          </th>
        );
      }
      return <></>;
    }

    function tableColumnGroups() {
      if (columnGroups === null) {
        return <></>; // not used by default
      } else {
        // tables can have some columns before the user defined ones, these need to be matched in column group initialization
        let tableColumnGroups = [
          rowExpander !== null ? (
            <th key={'row-expander-padding'}></th>
          ) : (
            <React.Fragment key={'no-row-expander-padding'}></React.Fragment>
          ),
          hasAllPageRowSelector() ? (
            <th key={'row-selector-padding'}></th>
          ) : (
            <React.Fragment key={'no-row-selector-padding'}></React.Fragment>
          ),
        ];
        for (let index = 0; index < columns.length; index++) {
          // manual iteration because we jump over indices sometimes
          let currentColumn = columns[index];
          let columnGroupForColumn =
            columnGroups.find(
              (columnGroup) =>
                columnGroup.startsAtAccessor === currentColumn.accessor
            ) || null;
          if (columnGroupForColumn !== null) {
            // if column needs group header, we add the element with text and width
            tableColumnGroups.push(
              <th
                scope="colgroup"
                key={'specified-thead' + index.toString()}
                colSpan={columnGroupForColumn.colspan}
                className={css['col-group'].concat(
                  ' ',
                  columnGroupForColumn.className
                    ? columnGroupForColumn.className
                    : ''
                )}
              >
                {columnGroupForColumn.header}
              </th>
            );
            index += columnGroupForColumn.colspan - 1;
          } else {
            // if column doesn't need group header, we just add empty default th
            tableColumnGroups.push(
              <th key={'default-thead' + index.toString()}></th>
            );
          }
        }
        return <tr>{tableColumnGroups}</tr>;
      }
    }

    return (
      <thead>
        {tableColumnGroups()}
        <tr>
          {showRemove && <th className={'w-14'}></th>}
          {rowExpander && <th></th>}
          {allPageRowSelector()}
          {columns.map((column) => (
            <th
              scope="col"
              key={column.accessor.toString()}
              className={column.headerClassName}
            >
              {column.sortable ? (
                <Link
                  to={(location) => ({
                    ...location,
                    search: search(location.search, column.accessor.toString()),
                  })}
                >
                  <FontAwesomeIcon icon={icon(column.accessor.toString())} />
                  {column.header}
                </Link>
              ) : (
                <>{column.header}</>
              )}
              {column.filter as any}
            </th>
          ))}
        </tr>
      </thead>
    );
  }

  function tbody() {
    function renderTdContent(row: T, column: TableColumn<T>) {
      const data = accessData(row, column);
      let td: any = data;

      const dataType = column.dataType;
      const noData = !data && data !== 0;

      if (typeof column.cell === 'function') {
        td = column.cell(row);
      } else {
        switch (true) {
          case column.editable && typeof column.editable === 'function':
            td = column.editable(row);
            break;
          case noData:
            td = '';
            break;
          case dataType === FieldTypes.BOOLEAN:
          case typeof data === 'boolean':
            td = data ? (
              <FontAwesomeIcon icon={faCheck} className="text-accent" />
            ) : (
              ''
            );
            break;
          case dataType === FieldTypes.JSON_AGG:
            td = (JSON.parse(td) as string[]).map((value, index) => (
              <div key={index}>{value}</div>
            ));
            break;
          case dataType === FieldTypes.OBJECT:
            td = <pre>{JSON.stringify(data, null, 1)}</pre>;
            break;
          case dataType === FieldTypes.DATE:
            td = moment(data).format('DD.MM.YYYY');
            break;
          case dataType === FieldTypes.DATE_TIME:
            td = moment(data).format('DD.MM.YYYY HH:mm:ss');
            break;
          case typeof data === 'object' && Array.isArray(data):
            td = data.join(', ');
            break;
          case RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/).test(
            data
          ):
          case RegExp(/^\d{4}-\d{2}-\d{2}$/).test(data):
            td = moment(data).format('DD.MM.YYYY');
            break;
          default:
            td = data;
        }
      }

      return td;
    }

    function open(row: T) {
      if (!rowDisabled(row) && authorizedViewRoute) {
        if (typeof authorizedViewRoute === 'function') {
          history.push(authorizedViewRoute(row));
        } else if (typeof authorizedViewRoute === 'string') {
          history.push(
            route(authorizedViewRoute, {
              [keyAccessor.toString()]: key(row).toString(),
            })
          );
        }
      }
    }

    function selectRow(row: T, event: React.ChangeEvent<HTMLInputElement>) {
      const setSelectedRows = selectedRowsState[1];
      if ((event.target as HTMLInputElement).checked) {
        setSelectedRows((rows) => rows.concat(row));
      } else {
        setSelectedRows((rows) =>
          rows.filter((anyRow) => key(anyRow) !== key(row))
        );
      }
    }

    function pageRowExpander(row: T) {
      if (rowExpander !== null) {
        return (
          <td className={css['row-expander']}>
            <div
              onClick={(event) => {
                if (expandedRows.indexOf(key(row).toString()) === -1) {
                  setExpandedRows((state) =>
                    state.concat([key(row).toString()])
                  );
                } else {
                  setExpandedRows((state) =>
                    state.filter((k) => k !== key(row).toString())
                  );
                }
                event.stopPropagation();
              }}
            >
              {expandedRows.indexOf(key(row).toString()) === -1 ? (
                <FontAwesomeIcon icon={faChevronRight} />
              ) : (
                <FontAwesomeIcon icon={faChevronDown} />
              )}
            </div>
          </td>
        );
      }
      return <></>;
    }

    function pageRowSelector(row: T, index: number) {
      if (selectedRowsState === null) {
        return <></>;
      } else {
        const selectedRows = selectedRowsState[0];
        return (
          <>
            <td className={'row-selector'} key={'id'}>
              <div {...tooltipContainerProps}>
                {tooltip}
                {!row[notShowSelectedChecked] && rowCheckable(row) && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.findIndex(
                        (anyRow) => key(anyRow) === key(row)
                      ) > -1
                    }
                    className={css['checkbox']}
                    data-index={index}
                    data-id={key(row)}
                    onChange={(event) => selectRow(row, event)}
                    onClick={(event) => event.stopPropagation()}
                  />
                )}
              </div>
            </td>
          </>
        );
      }
    }

    function pageRemoveRow(index: number) {
      if (showRemove) {
        return (
          <>
            <td className={'row-selector'} key={'id'}>
              <div {...tooltipContainerProps}>
                {tooltip}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onRemove?.(index, data);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faTimes}
                    color="#002A58"
                    width={8}
                    height={16}
                  />
                </button>
              </div>
            </td>
          </>
        );
      } else {
        return;
      }
    }

    function row(row: T, index: number) {
      return (
        <tr
          onMouseEnter={() => onMouseEnter(row)}
          onMouseLeave={() => onMouseLeave(row)}
          className={`table-row ${
            !rowDisabled(row) && authorizedViewRoute ? css['viewable'] : ''
          } ${rowClassSetter ? rowClassSetter(row) : ''}`}
          key={key(row)?.toString() || index}
          data-row-id={key(row)}
        >
          {pageRemoveRow(index)}
          {pageRowSelector(row, index)}
          {pageRowExpander(row)}
          {columns.map((column) => (
            <td
              className={`${column.className} ${
                column.onRowClick ? 'cursor-pointer' : ''
              }`}
              key={key(row)?.toString().concat('-', column.accessor.toString())}
              onClick={() =>
                column.onRowClick ? column.onRowClick(row) : open(row)
              }
            >
              {renderTdContent(row, column)}
            </td>
          ))}
        </tr>
      );
    }

    function rowExpansion(row: T) {
      const colSpan =
        columns.length + (selectedRowsState ? 1 : 0) + (rowExpander ? 1 : 0);
      const classNames = css['row-expansion'].concat(
        ' ',
        expandedRows.indexOf(key(row).toString()) > -1 ? css['expanded'] : ''
      );
      return (
        <tr
          className={classNames}
          key={'expansion'.concat(key(row).toString())}
        >
          <td colSpan={colSpan}>{rowExpander(row)}</td>
        </tr>
      );
    }
    function renderInTotalRow(rows: T[]) {
      function getTotal(accessor: string) {
        return rows.reduce((previousValue, currentValue) => {
          return (
            previousValue +
            (typeof currentValue[accessor] === 'string'
              ? +currentValue[accessor]
              : currentValue[accessor])
          );
        }, 0);
      }

      return (
        <tr id={'sum'} className={css['in-total-row']} data-row-id={'sum'}>
          {columns.map((column, index) => {
            if (index === 0) {
              return <td>{t('pages.warehouse.deals.total')}</td>;
            } else if (totalValueCols.includes(column.accessor.toString())) {
              const sum = getTotal(column.accessor.toString());
              return <td>{sum ? sum.toFixed(2) : null}</td>;
            } else {
              return <td></td>;
            }
          })}
        </tr>
      );
    }

    function rows() {
      let elements = [];
      // half indexes are hidden rows for expanded data
      for (let index = 0.0; index <= response.data.length - 0.5; index += 0.5) {
        if (index === Math.floor(index)) {
          elements.push(row(response.data[index], index));
        } else if (rowExpander !== null) {
          elements.push(rowExpansion(response.data[Math.floor(index)]));
        }
      }
      if (inTotal && totalValueCols) {
        elements.push(renderInTotalRow(response?.data));
      }
      return elements;
    }

    if (response === null) {
      return (
        <tbody className={css['loading']}>
          <tr>
            <td colSpan={showRemove ? columns.length + 1 : columns.length}>
              <div>{t('Loading')}</div>
              <Spinner visible={true} size="lg" />
            </td>
          </tr>
        </tbody>
      );
    } else if (response.data.length === 0) {
      return (
        <tbody className={css['without-data'] + ' request-completed'}>
          <tr>
            <td colSpan={showRemove ? columns.length + 1 : columns.length}>
              {t(noDataMessage)}
            </td>
          </tr>
        </tbody>
      );
    } else {
      return (
        <tbody className={css['with-data'] + ' request-completed'}>
          {rows()}
        </tbody>
      );
    }
  }

  function tfoot() {
    function aggregates() {
      if (!response?.aggregates || !showAggregates) {
        return null;
      }
      const aggs = response.aggregates;
      return (
        <>
          {Object.keys(aggs).map((aggKey, index) => (
            <tr
              className={`${css['aggregate']} ${aggKey}`}
              key={aggKey.concat(index.toString())}
            >
              <td
                className={css['aggregate-label']}
                key={aggKey.concat(index.toString(), 'label')}
              >
                {aggregateLabels && aggregateLabels[aggKey]}
              </td>
              {columns.map(
                (column, index) =>
                  index !== 0 && (
                    <td
                      className={css['aggregate-val']}
                      key={aggKey.concat(
                        index.toString(),
                        column.aggregateAccessor
                      )}
                    >
                      {aggs[aggKey][column.aggregateAccessor]}
                    </td>
                  )
              )}
            </tr>
          ))}
        </>
      );
    }

    return <tfoot>{aggregates()}</tfoot>;
  }

  function totals() {
    if (!response?.totals) {
      return null;
    }

    return (
      <div className={css['totals']}>
        {response.totals &&
          Object.keys(response.totals).map((key: string) => (
            <div className={css['total']} key={key} data-key={key}>
              <span className={css['total-col']}>{t(key)}:</span>
              <span className={css['total-val']}>{response.totals[key]}:</span>
            </div>
          ))}
      </div>
    );
  }

  function paging() {
    return response?.data?.length && paginate ? (
      <div className={css['table-paginator']}>
        <Paging response={response} paginate={paginate} />
      </div>
    ) : null;
  }

  function filteredTotals() {
    const renderFulltext = () => {
      if (filteredTotalsParams?.fullText?.keys) {
        let t = {
          count: response?.data ? response?.data.length : 0,
        };

        filteredTotalsParams?.fullText?.keys?.forEach((e) => {
          t = {
            ...t,
            [e.key]: e.value(response?.data),
          };
        });

        return filteredTotalsParams?.fullText.tp(
          filteredTotalsParams?.fullText.key,
          t
        );
      }

      return filteredTotalsParams?.fullText.tp(
        filteredTotalsParams?.fullText.key,
        { count: response?.data ? response?.data.length : 0 }
      );
    };

    return filteredTotalsParams ? (
      <div className={css['filtered-totals']}>
        {filteredTotalsParams?.fullText ? (
          renderFulltext()
        ) : (
          <>
            {t('table_total')} {response?.data ? response?.data.length : 0}{' '}
            {t(filteredTotalsParams?.recordName)}
          </>
        )}
      </div>
    ) : null;
  }

  const [toolbarComponent, setToolbarComponent] = useState(null);
  useEffect(() => {
    if (typeof toolbar === 'function') {
      setToolbarComponent(toolbar({ page, sort, desc, params }));
    }
  }, [toolbar, page, sort, desc, params]);

  if (typeof responseData === 'function') {
    responseData(response);
  }

  return (
    <>
      {toolbarComponent}
      {filteredTotals()}
      {tableAction}
      <div className={css['container']}>
        <div
          className={`${css['wrapper']} ${
            !columns.filter((c) => c.filter).length ? css['collapsed'] : ''
          }`}
        >
          <table
            className={`${css['table']} ${
              rowExpander ? css['expandable'] : ''
            }`}
          >
            {thead()}
            {tbody()}
            {tfoot()}
          </table>
        </div>
        {paging()}
        {totals()}
      </div>
    </>
  );
}
